import { getFormErrorMessage } from "@core/utility/get-error-message";
import { VehicleFormRule, VehicleFormSchema } from "@factory/form/form.rule";
import { useFormDefaultVehicle } from "@factory/form/form-default-vehicle.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseVehicleModel } from "@factory/model/query-model-vehicle";
import { 
  useQyDeleteVehicle, 
  useQyGetVehicleById, 
  useQyUpdateVehicle} from "@factory/query/vehicle.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditVehicle() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultVehicle();
  const navigate = useNavigate();
  const { mapUpdateVehicle, mapDeleteVehicle } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { vehicleId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Vehicle
  const handleGetApiSuccess = (data: GetResponseVehicleModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("tenant", responseData?.tenant || "");
      setValue("userCode", responseData?.user_code || "");
      setValue("plateNumber", responseData?.plate_number || "");
      setValue("make", responseData?.make || "");
      setValue("model", responseData?.model || "");
      setValue("year", responseData?.year);
      setValue("color", responseData?.color || "");
      setValue("nickname", responseData?.nickname || "");
      setValue("remarks", responseData?.remarks || "");
      setValue("avatar", responseData?.avatar || "");
      setValue("isBlocked", Boolean(responseData?.is_blocked));
  };
  const {
    data: vehicleResponse,
    isLoading,
    isError: vehicleError,
  } = useQyGetVehicleById(vehicleId || "", handleGetApiSuccess);
  const vehicleData = vehicleResponse?.data?.[0];

  // API UPDATE Vehicle
  const handleUpdateApiSuccess = () => {
    showSuccess("Vehicle updated successfully");
  };
  const { mutate: updateVehicle, isLoading: isEditLoading } = useQyUpdateVehicle(
    handleUpdateApiSuccess
  );

  // API DELETE Vehicle
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Vehicle deleted successfully");
  };
  const { mutate: deleteVehicle } = useQyDeleteVehicle(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<VehicleFormSchema>({
    defaultValues: getDefault(vehicleData),
    resolver: zodResolver(VehicleFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: VehicleFormSchema) => {
    if (!vehicleData || !vehicleData.code) {
      throw new Error("No vehicle data");
    }

    const formData = mapUpdateVehicle(form, vehicleData.code);
    updateVehicle(formData);
  };
  const handleValidateError = (err: FieldErrors<VehicleFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!vehicleData || !vehicleData.code) {
      throw new Error("No vehicle data found");
    }

    const form = mapDeleteVehicle(vehicleData.code);
    deleteVehicle(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    vehicleId,
    vehicleData,
    isLoading,
    vehicleError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}